/* Fixed artwork reads choices from the product's shared customization option set. */
(() => {
  class FixedFrameDesign extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.dialog = this.querySelector('dialog');
      this.form = this.closest('form');
      this.addEventListener('change', () => this.updatePreview(), { signal });
      this.querySelector('[data-design-open]').addEventListener('click', () => this.open(), { signal });
      this.querySelectorAll('[data-design-cancel]').forEach(button => button.addEventListener('click', () => this.cancel(), { signal }));
      this.querySelector('[data-design-save]').addEventListener('click', () => this.save(), { signal });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.cancel(); }, { signal });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.cancel(); }, { signal });
      this.form?.addEventListener('submit', event => {
        if (this.saved()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open();
      }, { capture: true, signal });
      this.artworkReady = false;
      this.loadArtwork();
      this.restore();
      // Quick-buy can replace the whole form on a variant change. Preserve only
      // committed selections for this product, never another product or drafts.
      this.form?.addEventListener('product:rerender', event => {
        if (event.detail.productChange) return;
        const fragment = event.detail.htmlFragment;
        const root = fragment.querySelector?.('template#quick-buy-content')?.content || fragment.content || fragment;
        const incoming = Array.from(root.querySelectorAll('fixed-frame-design'))
          .find(element => element.dataset.productId === this.dataset.productId);
        if (!incoming) return;
        for (const selector of ['[data-saved-background]', '[data-saved-thread]']) {
          incoming.querySelector(selector).value = this.querySelector(selector).value;
        }
      }, { signal, capture: true });
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.controller?.abort();
      if (this.artworkImage) { this.artworkImage.onload = null; this.artworkImage.onerror = null; }
    }
    loadArtwork() {
      const artwork = this.querySelector('[data-design-artwork]');
      const error = this.querySelector('[data-artwork-error]');
      let url;
      try {
        url = new URL(this.dataset.artworkUrl, window.location.origin);
        if (!['http:', 'https:'].includes(url.protocol)) throw new Error('Unsupported artwork URL');
      } catch {
        error.hidden = false;
        return;
      }
      const picture = new Image();
      this.artworkImage = picture;
      picture.crossOrigin = 'anonymous';
      picture.onload = () => {
        if (!this.isConnected) return;
        this.artworkReady = true;
        artwork.style.maskImage = `url(${JSON.stringify(url.href)})`;
        artwork.style.webkitMaskImage = artwork.style.maskImage;
        error.hidden = true;
        if (this.dialog.open) this.updatePreview();
        else this.restore();
      };
      picture.onerror = () => {
        this.artworkReady = false;
        error.hidden = false;
        this.restore();
      };
      picture.src = url.href;
    }
    saved() {
      return this.artworkReady && !!this.querySelector('[data-saved-background]').value && !!this.querySelector('[data-saved-thread]').value;
    }
    open() {
      if (this.dialog.open) return;
      this.restore();
      window.productAddOns?.beginCustomization(this.form);
      this.dialog.showModal();
      this.querySelector('[data-background]:checked')?.focus();
    }
    cancel() {
      window.productAddOns?.finishCustomization(this.form, false);
      this.restore();
      this.dialog.close();
      this.querySelector('[data-design-open]').focus();
    }
    restore() {
      for (const [selector, savedSelector] of [['[data-background]', '[data-saved-background]'], ['[data-thread]', '[data-saved-thread]']]) {
        const value = this.querySelector(savedSelector).value;
        this.querySelectorAll(selector).forEach((input, index) => {
          input.checked = value ? input.value === value : index === 0;
        });
      }
      this.querySelectorAll('[data-design-consent]').forEach(input => { input.checked = this.saved(); });
      this.form?.classList.toggle('fixed-design-saved', this.saved());
      const summary = this.querySelector('[data-design-summary]');
      summary.hidden = !this.saved();
      summary.textContent = this.saved() ? `${this.dataset.backgroundLabel || 'Background Color'}: ${this.querySelector('[data-saved-background]').value}; ${this.dataset.threadLabel || 'Thread Color'}: ${this.querySelector('[data-saved-thread]').value}` : '';
      this.querySelector('[data-design-open]').textContent = this.saved() ? 'Edit Customization' : 'Add Customization';
      this.updatePreview();
    }
    save() {
      if (this.querySelector('[data-design-save]').disabled || (window.productAddOns && !window.productAddOns.ready(this.form))) return;
      window.productAddOns?.finishCustomization(this.form, true);
      window.productAddOns?.saveSummary(this.form, this.querySelector('[data-design-add-on-summary]'));
      this.querySelector('[data-saved-background]').value = this.querySelector('[data-background]:checked').value;
      this.querySelector('[data-saved-thread]').value = this.querySelector('[data-thread]:checked').value;
      this.restore();
      this.dialog.close();
      this.querySelector('[data-design-open]').focus();
    }
    updatePreview() {
      const preview = this.querySelector('[data-design-preview]');
      const background = this.querySelector('[data-background]:checked');
      const thread = this.querySelector('[data-thread]:checked');
      if (!preview) return;
      preview.style.backgroundImage = background?.dataset.image ? `url("${background.dataset.image}")` : '';
      preview.style.backgroundColor = background?.dataset.image ? '' : background?.dataset.color || '';
      preview.style.color = thread?.dataset.color || '';
      const design = this.querySelector('[name="properties[Design]"]')?.value || 'Embroidery';
      preview.setAttribute('aria-label', `${design}, ${background?.value || 'no background selected'}, ${thread?.value || 'no thread selected'}`);
      this.querySelector('[data-design-save]').disabled = !this.artworkReady || !background || !thread || !Array.from(this.querySelectorAll('[data-design-consent]')).every(input => input.checked);
    }
  }
  if (!customElements.get('fixed-frame-design')) customElements.define('fixed-frame-design', FixedFrameDesign);
})();
